import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Tab, Tabs, Divider,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  TrendingUp as EarnedIcon,
  TrendingDown as WithdrawnIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import adminService from '../../services/adminService';

const StatCard = ({ title, value, icon, color }) => (
  <Card elevation={2}>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="body2" color="text.secondary">{title}</Typography>
          <Typography variant="h5" fontWeight="bold" color={color || 'text.primary'}>{value}</Typography>
        </Box>
        <Box sx={{ color: color || 'primary.main', opacity: 0.7 }}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

const AdminWallet = () => {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({ amount: '', bank_name: '', account_number: '', account_name: '', notes: '' });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [w, t, wd] = await Promise.all([
        adminService.getWallet(),
        adminService.getWalletTransactions(),
        adminService.getWithdrawals(),
      ]);
      setWallet(w);
      setTransactions(t.items || []);
      setWithdrawals(wd);
    } catch { toast.error('Failed to load wallet data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawForm.amount);
    if (!amount || amount <= 0) { toast.error('Enter a valid amount'); return; }
    if (!withdrawForm.bank_name || !withdrawForm.account_number || !withdrawForm.account_name) {
      toast.error('All bank details are required'); return;
    }
    setSaving(true);
    try {
      await adminService.requestWithdrawal({ ...withdrawForm, amount });
      toast.success('Withdrawal request submitted');
      setWithdrawOpen(false);
      setWithdrawForm({ amount: '', bank_name: '', account_number: '', account_name: '', notes: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit withdrawal'); }
    finally { setSaving(false); }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await adminService.updateWithdrawalStatus(id, status);
      toast.success(`Withdrawal ${status}`);
      fetchAll();
    } catch { toast.error('Failed to update status'); }
  };

  const statusColor = (s) => s === 'completed' ? 'success' : s === 'rejected' ? 'error' : 'warning';

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">Platform Wallet</Typography>
        <Button variant="contained" onClick={() => setWithdrawOpen(true)} disabled={!wallet || wallet.balance <= 0}>
          Request Withdrawal
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Available Balance" value={`KES ${(wallet?.balance || 0).toLocaleString()}`}
            icon={<WalletIcon sx={{ fontSize: 36 }} />} color="primary.main" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Earned" value={`KES ${(wallet?.total_earned || 0).toLocaleString()}`}
            icon={<EarnedIcon sx={{ fontSize: 36 }} />} color="success.main" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Withdrawn" value={`KES ${(wallet?.total_withdrawn || 0).toLocaleString()}`}
            icon={<WithdrawnIcon sx={{ fontSize: 36 }} />} color="warning.main" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Revenue Sources" value={wallet?.landlord_breakdown?.length || 0}
            icon={<BusinessIcon sx={{ fontSize: 36 }} />} />
        </Grid>
      </Grid>

      {/* Landlord Breakdown */}
      {wallet?.landlord_breakdown?.length > 0 && (
        <Card sx={{ mb: 3 }} elevation={1}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Revenue by Landlord</Typography>
            <Divider sx={{ mb: 1 }} />
            {wallet.landlord_breakdown.map(lb => (
              <Box key={lb.landlord_id} display="flex" justifyContent="space-between" py={0.5}>
                <Typography>{lb.landlord_name}</Typography>
                <Typography fontWeight="bold" color="success.main">KES {lb.total_commission.toLocaleString()}</Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Tabs: Transactions / Withdrawals */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Transactions" />
        <Tab label="Withdrawals" />
      </Tabs>

      {tab === 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                {['Date','Type','Amount','Landlord','Description'].map(h => <TableCell key={h}><strong>{h}</strong></TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map(t => (
                <TableRow key={t.id} hover>
                  <TableCell>{new Date(t.created_at).toLocaleDateString()}</TableCell>
                  <TableCell><Chip label={t.type} color={t.type === 'credit' ? 'success' : 'error'} size="small" /></TableCell>
                  <TableCell>KES {Number(t.amount).toLocaleString()}</TableCell>
                  <TableCell>{t.landlord_name || '—'}</TableCell>
                  <TableCell>{t.description}</TableCell>
                </TableRow>
              ))}
              {transactions.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}>No transactions yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 1 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                {['Date','Amount','Bank','Account','Status','Actions'].map(h => <TableCell key={h}><strong>{h}</strong></TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {withdrawals.map(w => (
                <TableRow key={w.id} hover>
                  <TableCell>{new Date(w.requested_at).toLocaleDateString()}</TableCell>
                  <TableCell>KES {Number(w.amount).toLocaleString()}</TableCell>
                  <TableCell>{w.bank_name}</TableCell>
                  <TableCell>{w.account_name} — {w.account_number}</TableCell>
                  <TableCell><Chip label={w.status} color={statusColor(w.status)} size="small" /></TableCell>
                  <TableCell>
                    {w.status === 'pending' && (
                      <Box display="flex" gap={1}>
                        <Button size="small" variant="outlined" color="success" onClick={() => handleUpdateStatus(w.id, 'completed')}>Complete</Button>
                        <Button size="small" variant="outlined" color="error" onClick={() => handleUpdateStatus(w.id, 'rejected')}>Reject</Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {withdrawals.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}>No withdrawals yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Withdrawal Dialog */}
      <Dialog open={withdrawOpen} onClose={() => setWithdrawOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Withdrawal</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Available balance: <strong>KES {(wallet?.balance || 0).toLocaleString()}</strong>
          </Alert>
          <TextField fullWidth margin="dense" label="Amount (KES)" type="number" value={withdrawForm.amount}
            onChange={e => setWithdrawForm({ ...withdrawForm, amount: e.target.value })}
            inputProps={{ min: 1, max: wallet?.balance || 0 }} />
          <TextField fullWidth margin="dense" label="Bank Name" value={withdrawForm.bank_name}
            onChange={e => setWithdrawForm({ ...withdrawForm, bank_name: e.target.value })} />
          <TextField fullWidth margin="dense" label="Account Number" value={withdrawForm.account_number}
            onChange={e => setWithdrawForm({ ...withdrawForm, account_number: e.target.value })} />
          <TextField fullWidth margin="dense" label="Account Name" value={withdrawForm.account_name}
            onChange={e => setWithdrawForm({ ...withdrawForm, account_name: e.target.value })} />
          <TextField fullWidth margin="dense" label="Notes (optional)" multiline rows={2} value={withdrawForm.notes}
            onChange={e => setWithdrawForm({ ...withdrawForm, notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleWithdraw} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminWallet;
